import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import productMasterApi, { PrdProduct, CreateRequest } from '../../../services/productMasterApi';

type Mode = 'browse' | 'add' | 'edit';

export function useProductMasterForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<PrdProduct[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<PrdProduct | null>(null);

  // Form fields
  const [prdCode, setPrdCode] = useState('');
  const [prdName, setPrdName] = useState('');
  const [fullPrdCode, setFullPrdCode] = useState('');
  const [unit, setUnit] = useState('');
  const [prdSort, setPrdSort] = useState('');
  const [pcsCode, setPcsCode] = useState('');
  const [processing, setProcessing] = useState('');
  const [speci, setSpeci] = useState('');
  const [component, setComponent] = useState('');
  const [org, setOrg] = useState('');
  const [uWidthC, setUWidthC] = useState(0);
  const [fWidthC, setFWidthC] = useState(0);
  const [wightM, setWightM] = useState(0);
  const [colorCode, setColorCode] = useState('');
  const [color, setColor] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [memo, setMemo] = useState('');
  const [cUser, setCUser] = useState('');
  const [cDate, setCDate] = useState<string | null>(null);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await productMasterApi.list(1, 500);
      setRecords(res.data || []);
      if ((res.data || []).length > 0) {
        setCurrentIdx(0);
      }
    } catch {
      message.error('載入資料失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const syncFields = useCallback((rec: PrdProduct) => {
    setCurrentRecord(rec);
    setPrdCode(rec.prd_code || '');
    setPrdName(rec.prd_name || '');
    setFullPrdCode(rec.full_prd_code || '');
    setUnit(rec.unit || '');
    setPrdSort(rec.prd_sort || '');
    setPcsCode(rec.pcs_code || '');
    setProcessing(rec.processing || '');
    setSpeci(rec.speci || '');
    setComponent(rec.component || '');
    setOrg(rec.org || '');
    setUWidthC(rec.u_width_c || 0);
    setFWidthC(rec.f_width_c || 0);
    setWightM(rec.wight_m || 0);
    setColorCode(rec.color_code || '');
    setColor(rec.color || '');
    setIsActive(rec.is_active);
    setMemo(rec.memo || '');
    setCUser(rec.c_user || '');
    setCDate(rec.c_date);
  }, []);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  useEffect(() => {
    if (records.length > 0 && currentIdx >= 0 && currentIdx < records.length) {
      syncFields(records[currentIdx]);
    }
  }, [records, currentIdx, syncFields]);

  // Navigation
  const navFirst = useCallback(() => setCurrentIdx(0), []);
  const navPrev = useCallback(() => setCurrentIdx((i) => Math.max(0, i - 1)), []);
  const navNext = useCallback(() => setCurrentIdx((i) => Math.min(records.length - 1, i + 1)), [records.length]);
  const navLast = useCallback(() => setCurrentIdx(records.length - 1), [records.length]);

  const startAdd = useCallback(() => {
    setMode('add');
    setCurrentRecord(null);
    setPrdCode(''); setPrdName(''); setFullPrdCode(''); setUnit('');
    setPrdSort(''); setPcsCode(''); setProcessing(''); setSpeci('');
    setComponent(''); setOrg(''); setUWidthC(0); setFWidthC(0);
    setWightM(0); setColorCode(''); setColor('');
    setIsActive(true); setMemo('');
    setCUser(''); setCDate(null);
  }, []);

  const startEdit = useCallback(() => { setMode('edit'); }, []);

  const cancel = useCallback(() => {
    setMode('browse');
    if (records.length > 0) {
      syncFields(records[currentIdx]);
    }
  }, [records, currentIdx, syncFields]);

  const buildPayload = useCallback((): CreateRequest => ({
    master: {
      prd_code: prdCode.trim(),
      prd_name: prdName.trim(),
      full_prd_code: fullPrdCode.trim(),
      unit: unit.trim(),
      prd_sort: prdSort,
      pcs_code: pcsCode,
      processing,
      speci,
      component,
      org,
      u_width_c: uWidthC,
      f_width_c: fWidthC,
      wight_m: wightM,
      color_code: colorCode,
      color,
      is_active: isActive,
      memo,
    },
  }), [prdCode, prdName, fullPrdCode, unit, prdSort, pcsCode, processing, speci,
       component, org, uWidthC, fWidthC, wightM, colorCode, color, isActive, memo]);

  const save = useCallback(async () => {
    setLoading(true);
    try {
      const payload = buildPayload();
      if (mode === 'add') {
        await productMasterApi.create(payload);
        message.success('新增成功');
      } else {
        const id = currentRecord?.prd_mid;
        if (!id) { message.error('無法取得記錄ID'); return; }
        await productMasterApi.update(id, payload);
        message.success('儲存成功');
      }
      setMode('browse');
      await loadRecords();
    } catch (err: any) {
      if (err?.response?.status === 422) {
        const errors = err.response.data?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          message.error(errors[0].message);
        }
      } else {
        message.error('儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [mode, currentRecord, buildPayload, loadRecords]);

  const doDelete = useCallback(async () => {
    const id = currentRecord?.prd_mid;
    if (!id) return;
    setLoading(true);
    try {
      await productMasterApi.delete(id);
      message.success('刪除成功');
      await loadRecords();
    } catch (err: any) {
      if (err?.response?.status === 422) {
        const errors = err.response.data?.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          message.error(errors[0].message);
        } else {
          message.error('刪除失敗');
        }
      } else {
        message.error('刪除失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [currentRecord, loadRecords]);

  const quickSearch = useCallback((keyword: string) => {
    const idx = records.findIndex((r) =>
      r.prd_code.toUpperCase().includes(keyword.toUpperCase()) ||
      r.full_prd_code.toUpperCase().includes(keyword.toUpperCase()) ||
      r.prd_name.toUpperCase().includes(keyword.toUpperCase())
    );
    if (idx >= 0) {
      setCurrentIdx(idx);
    } else {
      message.warning('查無資料');
    }
  }, [records]);

  return {
    mode, loading, records, currentIdx, currentRecord,
    prdCode, prdName, fullPrdCode, unit, prdSort,
    pcsCode, processing, speci, component, org,
    uWidthC, fWidthC, wightM, colorCode, color,
    isActive, memo, cUser, cDate,
    setPrdCode, setPrdName, setFullPrdCode, setUnit, setPrdSort,
    setPcsCode, setProcessing, setSpeci, setComponent, setOrg,
    setUWidthC, setFWidthC, setWightM, setColorCode, setColor,
    setIsActive, setMemo,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete,
    quickSearch,
    recordCount: records.length,
    recordPosition: records.length > 0 ? currentIdx + 1 : 0,
  };
}
